// @ts-nocheck
import { logger as sharedLogger } from '@/core/logger';
import { Platform } from 'react-native';

const silentLogger = {
    warn() {},
    info() {},
    error() {},
    debug() {},
    trace() {},
};

// Web must not write to the browser console. Native logging stays unchanged.
const logger = Platform.OS === 'web' ? silentLogger : sharedLogger;

const SCRIPT_ID = 'dynatrace-web-agent';
const WEB_SCRIPT_URL = process.env.EXPO_PUBLIC_DYNATRACE_WEB_SCRIPT_URL;

let privacyOptionsApplied = false;

// Conditional import for native platforms only (prevents web build crashes)
let Dynatrace: any = null;
if (Platform.OS !== 'web') {
    try {
        // Safe to require on native; will not execute on web
        const dynatraceModule = require('@dynatrace/react-native-plugin');
        Dynatrace = dynatraceModule.Dynatrace;
    } catch (error) {
        logger.warn('DynatraceService', 'Failed to load Dynatrace module', { error });
    }
}

function injectWebAgent(): void {
    if (Platform.OS !== 'web') return;
    if (!WEB_SCRIPT_URL) {
        logger.warn('DynatraceService', 'WEB_SCRIPT_URL missing; skipping injection');
        return;
    }
    if (document.getElementById(SCRIPT_ID)) {
        return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = WEB_SCRIPT_URL;
    script.crossOrigin = 'anonymous';
    script.async = true;
    document.head.appendChild(script);
    logger.info('DynatraceService', 'Web agent injected');
}

/**
 * Apply native privacy consent with UserBehavior level.
 * This is the main initialization step for Dynatrace.
 */
async function applyNativePrivacyOptions(): Promise<void> {
    if (privacyOptionsApplied || Platform.OS === 'web') return;

    try {
        // Wait a bit for Dynatrace to initialize (since autoStart is enabled)
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Use the conditionally imported Dynatrace module
        if (!Dynatrace) {
            logger.warn('DynatraceService', 'Dynatrace module not available');
            return;
        }

        // Dynamically import DataCollectionLevel and UserPrivacyOptions when needed
        const { DataCollectionLevel, UserPrivacyOptions } = require('@dynatrace/react-native-plugin');

        if (!Dynatrace?.applyUserPrivacyOptions) {
            logger.warn('DynatraceService', 'applyUserPrivacyOptions not available');
            return;
        }

        if (!UserPrivacyOptions) {
            logger.warn('DynatraceService', 'UserPrivacyOptions class not available');
            return;
        }

        // Create privacy config with UserBehavior level and crash reporting enabled
        const privacyConfig = new UserPrivacyOptions(DataCollectionLevel.UserBehavior, true);

        // Apply the privacy options
        Dynatrace.applyUserPrivacyOptions(privacyConfig);

        privacyOptionsApplied = true;
        logger.info('DynatraceService', 'Privacy options set to UserBehavior');
    } catch (err) {
        logger.error('DynatraceService', 'Error setting Dynatrace privacy options', { err, stack: err?.stack });
    }
}

/**
 * Initialize Dynatrace.
 * Native: Applies UserBehavior privacy options (assumes autoStart is enabled).
 * Web: Injects script.
 */
export async function initDynatrace(): Promise<void> {
    try {
        if (Platform.OS === 'web') {
            injectWebAgent();
        } else {
            // Apply privacy options with UserBehavior level
            await applyNativePrivacyOptions();
        }
    } catch (error) {
        logger.error('DynatraceService', 'Failed to initialize Dynatrace', { error, stack: error?.stack });
    }
}

/**
 * Track custom Dynatrace events with attributes.
 * Uses Dynatrace.enterManualAction() for Classic RUM (native) and dtrum.enterAction for web.
 * 
 * Standard payload keys:
 * - p_screen: Screen name (login, signup, agencyInfo, agencyAddress, bankDetails, companyPersonnel, legalDocs, otp, selectUser, etc.)
 * - p_actionName: Label name of the button/link that triggered the action (e.g., "Login", "Submit", "Next")
 * - p_sectionName: Section name (domesticAgencyRegistration, internationalAgencyRegistration, etc.)
 * - p_agencyType: Agency type (domestic, international)
 * - p_failedMessage: Error message from API when action fails
 * - eventType: 'view' | 'action' | 'error'
 * 
 * Additional custom properties can be included (e.g., paymentId, propertyId, amount, etc.)
 * 
 * IMPORTANT: Custom properties must be registered in Dynatrace UI:
 * Settings > Web and mobile monitoring > User session and action properties
 * Add each property key (e.g., p_screen, paymentId, amount) as "SDK reported value"
 */
function isReportableDynatraceValue(value: unknown): boolean {
    if (value === undefined || value === null) {
        return false;
    }
    if (typeof value === 'string') {
        return value.trim().length > 0;
    }
    return true;
}

function firstNonEmptyText(values: Array<string | null | undefined>): string | null {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) {
            return trimmed;
        }
    }
    return null;
}

function formatIdentityPhone(
    phone: string | null | undefined,
    countryCode: string | null | undefined,
): string | null {
    const number = phone?.trim();
    if (!number) {
        return null;
    }
    if (number.startsWith('+')) {
        return number;
    }
    const code = countryCode?.trim();
    if (!code) {
        return number;
    }
    const normalizedCode = code.startsWith('+') ? code : `+${code}`;
    return `${normalizedCode}${number}`;
}

export interface DynatraceUserIdentitySource {
    primary_email?: string | null;
    secondary_email?: string | null;
    tertiary_email?: string | null;
    primary_phone?: string | null;
    secondary_phone?: string | null;
    tertiary_phone?: string | null;
    primary_country_code?: string | null;
    secondary_country_code?: string | null;
    tertiary_country_code?: string | null;
}

/**
 * Email when the user has one. Otherwise the first phone number.
 * Returns null when both are missing so callers do not identify an empty user.
 */
export function resolveDynatraceUserIdentity(
    user: DynatraceUserIdentitySource | null | undefined,
): string | null {
    if (!user) {
        return null;
    }
    const email = firstNonEmptyText([
        user.primary_email,
        user.secondary_email,
        user.tertiary_email,
    ]);
    if (email) {
        return email.toLowerCase();
    }
    return (
        formatIdentityPhone(user.primary_phone, user.primary_country_code) ??
        formatIdentityPhone(user.secondary_phone, user.secondary_country_code) ??
        formatIdentityPhone(user.tertiary_phone, user.tertiary_country_code)
    );
}

/** Verification logging disabled. Call sites stay so tracking flow is unchanged. */
function logDynatraceTrack(
    _outcome: 'attempt' | 'sent' | 'skipped',
    _details: Record<string, unknown>,
): void {
    // Intentionally a no-op: verification logging is disabled.
}

export function trackDynatraceEvent(payload: {
    p_screen?: string | undefined;
    p_actionName?: string | undefined;
    p_sectionName?: string | undefined;
    p_agencyType?: string | undefined;
    p_failedMessage?: string | undefined;
    eventType?: 'view' | 'action' | 'error' | undefined;
    [key: string]: string | number | boolean | undefined; // Allow additional custom properties
}): void {
    try {
        // Use p_actionName as the action label, fallback to screen name with proper formatting
        const actionLabel = payload.p_actionName 
            || (payload.p_screen ? `${payload.p_screen.charAt(0).toUpperCase() + payload.p_screen.slice(1)} Screen Viewed` : 'app-event');

        logDynatraceTrack('attempt', {
            actionLabel,
            p_screen: payload.p_screen,
            p_actionName: payload.p_actionName,
            eventType: payload.eventType ?? 'action',
            payload,
        });

        if (Platform.OS === 'web') {
            // Web: Use dtrum.enterAction
            // Wait for dtrum to be ready before using it
            if (typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined') {
                const dtrum = (window as any).dtrum;
                
                // Use dtrum.ready() to ensure the agent is fully loaded
                if (dtrum.ready && typeof dtrum.ready === 'function') {
                    dtrum.ready(() => {
                        try {
                            if (dtrum.enterAction) {
                                const action = dtrum.enterAction(actionLabel);
                                
                                // Report all payload properties as values
                                Object.entries(payload).forEach(([key, value]) => {
                                    if (isReportableDynatraceValue(value)) {
                                        if (dtrum.reportValue && typeof dtrum.reportValue === 'function') {
                                            dtrum.reportValue(action, key, String(value));
                                        } else if (dtrum.reportStringValue && typeof dtrum.reportStringValue === 'function') {
                                            dtrum.reportStringValue(action, key, String(value));
                                        }
                                    }
                                });
                                
                                if (dtrum.leaveAction && typeof dtrum.leaveAction === 'function') {
                                    dtrum.leaveAction(action);
                                }
                                
                                logger.debug('DynatraceService', 'Web custom action tracked', { actionLabel, payload });
                                logDynatraceTrack('sent', { actionLabel, channel: 'web-dtrum-ready' });
                            } else {
                                logger.warn('DynatraceService', 'dtrum.enterAction not available');
                                logDynatraceTrack('skipped', { actionLabel, reason: 'dtrum.enterAction missing (ready callback)' });
                            }
                        } catch (err) {
                            logger.error('DynatraceService', 'Error tracking web custom action', { 
                                actionLabel, 
                                payload, 
                                err,
                                errorMessage: err?.message,
                                errorStack: err?.stack 
                            });
                            logDynatraceTrack('skipped', {
                                actionLabel,
                                reason: 'web track exception (ready)',
                                errorMessage: err?.message,
                                payload,
                            });
                        }
                    });
                } else {
                    // Fallback: try to use dtrum directly if ready() is not available
                    try {
                        if (dtrum.enterAction) {
                            const action = dtrum.enterAction(actionLabel);
                            Object.entries(payload).forEach(([key, value]) => {
                                if (isReportableDynatraceValue(value)) {
                                    if (dtrum.reportValue && typeof dtrum.reportValue === 'function') {
                                        dtrum.reportValue(action, key, String(value));
                                    } else if (dtrum.reportStringValue && typeof dtrum.reportStringValue === 'function') {
                                        dtrum.reportStringValue(action, key, String(value));
                                    }
                                }
                            });
                            if (dtrum.leaveAction && typeof dtrum.leaveAction === 'function') {
                                dtrum.leaveAction(action);
                            }
                            logger.debug('DynatraceService', 'Web custom action tracked (fallback)', { actionLabel, payload });
                            logDynatraceTrack('sent', { actionLabel, channel: 'web-dtrum-fallback' });
                        } else {
                            logger.warn('DynatraceService', 'dtrum.enterAction not available (fallback)');
                            logDynatraceTrack('skipped', { actionLabel, reason: 'dtrum.enterAction missing (fallback)' });
                        }
                    } catch (err) {
                        logger.error('DynatraceService', 'Error tracking web custom action (fallback)', { 
                            actionLabel, 
                            payload, 
                            err,
                            errorMessage: err?.message,
                            errorStack: err?.stack 
                        });
                        logDynatraceTrack('skipped', {
                            actionLabel,
                            reason: 'web track exception (fallback)',
                            errorMessage: err?.message,
                            payload,
                        });
                    }
                }
            } else {
                logger.warn('DynatraceService', 'dtrum not available on web - script may not be loaded yet', { 
                    hasWindow: typeof window !== 'undefined',
                    hasDtrum: typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined'
                });
                logDynatraceTrack('skipped', {
                    actionLabel,
                    reason: 'dtrum not loaded (check EXPO_PUBLIC_DYNATRACE_WEB_SCRIPT_URL)',
                });
            }
        } else if (Dynatrace) {
            // Mobile: Use enterManualAction for Classic RUM support
            if (!Dynatrace.enterManualAction) {
                logger.warn('DynatraceService', 'Dynatrace.enterManualAction not available');
                logDynatraceTrack('skipped', { actionLabel, reason: 'enterManualAction missing' });
                return;
            }

            try {
                const action = Dynatrace.enterManualAction(actionLabel);
                
                if (!action) {
                    logger.warn('DynatraceService', 'Failed to create Dynatrace manual action', { actionLabel });
                    logDynatraceTrack('skipped', { actionLabel, reason: 'enterManualAction returned null' });
                    return;
                }

                // Debug: Log available methods on action object
                const availableMethods = Object.keys(action).filter(key => typeof action[key] === 'function');
                logger.debug('DynatraceService', 'Action object methods', { actionLabel, availableMethods });

                // Report all payload properties as values
                // Use type-specific methods: reportStringValue, reportIntValue, reportDoubleValue
                Object.entries(payload).forEach(([key, value]) => {
                    if (isReportableDynatraceValue(value)) {
                        try {
                            if (typeof value === 'string') {
                                // Use reportStringValue for strings
                                if (action.reportStringValue && typeof action.reportStringValue === 'function') {
                                    action.reportStringValue(key, value);
                                } else {
                                    logger.warn('DynatraceService', 'action.reportStringValue not available', { 
                                        key, 
                                        value,
                                        hasMethod: !!action.reportStringValue,
                                        methodType: typeof action.reportStringValue,
                                        availableMethods 
                                    });
                                }
                            } else if (typeof value === 'number') {
                                // Use reportIntValue for integers, reportDoubleValue for floats
                                if (Number.isInteger(value)) {
                                    if (action.reportIntValue && typeof action.reportIntValue === 'function') {
                                        action.reportIntValue(key, value);
                                    } else {
                                        logger.warn('DynatraceService', 'action.reportIntValue not available', { 
                                            key, 
                                            value,
                                            hasMethod: !!action.reportIntValue,
                                            methodType: typeof action.reportIntValue 
                                        });
                                    }
                                } else {
                                    if (action.reportDoubleValue && typeof action.reportDoubleValue === 'function') {
                                        action.reportDoubleValue(key, value);
                                    } else {
                                        logger.warn('DynatraceService', 'action.reportDoubleValue not available', { 
                                            key, 
                                            value,
                                            hasMethod: !!action.reportDoubleValue,
                                            methodType: typeof action.reportDoubleValue 
                                        });
                                    }
                                }
                            } else if (typeof value === 'boolean') {
                                // Convert boolean to string and use reportStringValue
                                if (action.reportStringValue && typeof action.reportStringValue === 'function') {
                                    action.reportStringValue(key, String(value));
                                } else {
                                    logger.warn('DynatraceService', 'action.reportStringValue not available (boolean)', { key });
                                }
                            } else {
                                // Convert objects/other types to JSON string
                                if (action.reportStringValue && typeof action.reportStringValue === 'function') {
                                    action.reportStringValue(key, JSON.stringify(value));
                                } else {
                                    logger.warn('DynatraceService', 'action.reportStringValue not available (object)', { key });
                                }
                            }
                        } catch (err) {
                            logger.warn('DynatraceService', 'Failed to report value for action', { 
                                key, 
                                value, 
                                err,
                                errorMessage: err?.message,
                                errorStack: err?.stack 
                            });
                        }
                    }
                });

                // End the action - this sends the event to Dynatrace
                if (action.leaveAction && typeof action.leaveAction === 'function') {
                    action.leaveAction();
                } else {
                    logger.warn('DynatraceService', 'action.leaveAction not available', { availableMethods });
                }
                
                logger.debug('DynatraceService', 'Dynatrace manual action tracked', { actionLabel, payload });
                logDynatraceTrack('sent', { actionLabel, channel: 'native-manual-action' });
            } catch (err) {
                logger.error('DynatraceService', 'Error tracking Dynatrace manual action', { 
                    payload, 
                    err,
                    errorMessage: err?.message,
                    errorStack: err?.stack,
                    actionLabel 
                });
                logDynatraceTrack('skipped', {
                    actionLabel,
                    reason: 'native track exception',
                    errorMessage: err?.message,
                });
            }
        } else {
            logger.warn('DynatraceService', 'Dynatrace module not available for native platform');
            logDynatraceTrack('skipped', { actionLabel, reason: 'native Dynatrace module unavailable' });
        }
    } catch (error) {
        logger.warn('DynatraceService', 'Failed to track event', { payload, error });
        logDynatraceTrack('skipped', {
            actionLabel: payload.p_actionName ?? payload.p_screen ?? 'app-event',
            reason: 'trackDynatraceEvent outer catch',
            error,
        });
    }
}

/**
 * Set user email for Dynatrace user identification.
 */
export async function setDynatraceUserEmail(email?: string | null): Promise<void> {
    const trimmed = email?.trim() ?? '';
    if (!trimmed) {
        return;
    }
    const userEmail = trimmed.includes('@') ? trimmed.toLowerCase() : trimmed;
    try {
        if (Platform.OS === 'web') {
            if (typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined') {
                const dtrum = (window as any).dtrum;
                
                if (dtrum.ready && typeof dtrum.ready === 'function') {
                    dtrum.ready(() => {
                        if (dtrum.identifyUser && typeof dtrum.identifyUser === 'function') {
                            dtrum.identifyUser(userEmail);
                            logger.debug('DynatraceService', 'User identified on web', { email: userEmail });
                        }
                    });
                } else if (dtrum.identifyUser && typeof dtrum.identifyUser === 'function') {
                    // Fallback: use directly if ready() is not available
                    dtrum.identifyUser(userEmail);
                    logger.debug('DynatraceService', 'User identified on web (fallback)', { email: userEmail });
                }
            } else {
                logger.warn('DynatraceService', 'dtrum not available for user identification', { 
                    hasWindow: typeof window !== 'undefined',
                    hasDtrum: typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined'
                });
            }
        } else {
            // Use the conditionally imported Dynatrace module
            if (Dynatrace?.identifyUser) {
                await Dynatrace.identifyUser(userEmail);
            }
        }
    } catch (err) {
        logger.warn('DynatraceService', 'Failed to identify user', { err, email });
    }
}

/**
 * Clear user identification in Dynatrace.
 */
export async function clearDynatraceUser(): Promise<void> {
    try {
        if (Platform.OS === 'web') {
            if (typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined') {
                const dtrum = (window as any).dtrum;
                
                if (dtrum.ready && typeof dtrum.ready === 'function') {
                    dtrum.ready(() => {
                        if (dtrum.identifyUser && typeof dtrum.identifyUser === 'function') {
                            dtrum.identifyUser('');
                            logger.debug('DynatraceService', 'User cleared on web');
                        }
                    });
                } else if (dtrum.identifyUser && typeof dtrum.identifyUser === 'function') {
                    // Fallback: use directly if ready() is not available
                    dtrum.identifyUser('');
                    logger.debug('DynatraceService', 'User cleared on web (fallback)');
                }
            } else {
                logger.warn('DynatraceService', 'dtrum not available for clearing user', { 
                    hasWindow: typeof window !== 'undefined',
                    hasDtrum: typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined'
                });
            }
        } else {
            // Use the conditionally imported Dynatrace module
            if (Dynatrace?.identifyUser) {
                await Dynatrace.identifyUser('');
            }
        }
    } catch (err) {
        logger.warn('DynatraceService', 'Failed to clear user', { err });
    }
}

/**
 * Report errors to Dynatrace.
 * On native: Uses reportCrashWithException() for crashes with Error objects, reportCrash() for crashes with stack traces,
 *            reportErrorStacktrace() for errors with stack traces, reportError() for simple errors
 * On web: Uses dtrum.reportError() for JavaScript errors
 * 
 * Reference: https://www.npmjs.com/package/@dynatrace/react-native-plugin#manually-report-a-crash
 * 
 * Note: reportCrashWithException and reportCrash will force the session to be completed.
 * Any new actions captured afterwards will be added into a new session.
 */
export function reportDynatraceError(message: string, error?: any, context?: Record<string, any>): void {
    try {
        if (Platform.OS === 'web') {
            if (typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined') {
                const dtrum = (window as any).dtrum;
                
                if (dtrum.ready && typeof dtrum.ready === 'function') {
                    dtrum.ready(() => {
                        if (dtrum.reportError && typeof dtrum.reportError === 'function') {
                            dtrum.reportError(message, error || context);
                            logger.debug('DynatraceService', 'Error reported on web', { message });
                        }
                    });
                } else if (dtrum.reportError && typeof dtrum.reportError === 'function') {
                    // Fallback: use directly if ready() is not available
                    dtrum.reportError(message, error || context);
                    logger.debug('DynatraceService', 'Error reported on web (fallback)', { message });
                }
            } else {
                logger.warn('DynatraceService', 'dtrum not available for error reporting', { 
                    hasWindow: typeof window !== 'undefined',
                    hasDtrum: typeof window !== 'undefined' && typeof (window as any).dtrum !== 'undefined'
                });
            }
        } else {
            // Native: Report JavaScript errors manually
            // Use the conditionally imported Dynatrace module
            if (!Dynatrace) {
                logger.warn('DynatraceService', 'Dynatrace module not available');
                return;
            }

            try {
                // Extract stack trace from error object
                let stackTrace: string | undefined;
                let errorObj: Error | undefined;

                if (error instanceof Error) {
                    errorObj = error;
                    stackTrace = error.stack;
                } else if (error?.stack) {
                    stackTrace = error.stack;
                }

                // Build error message with context if available
                let errorMessage = message;
                if (context && Object.keys(context).length > 0) {
                    const contextStr = Object.entries(context)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                    errorMessage = `${message} | Context: ${contextStr}`;
                }

                const errorName = context?.errorType || message || 'JavaScriptError';
                const errorCode = context?.status || context?.code || 500;

                // Priority 1: Use reportCrashWithException if we have an Error object with stack trace
                // This is preferred for crashes as it provides better error details
                if (errorObj && stackTrace && Dynatrace.reportCrashWithException) {
                    const crashName = errorName;
                    Dynatrace.reportCrashWithException(crashName, errorObj);
                }
                // Priority 2: Use reportCrash if we have a stack trace
                else if (stackTrace && Dynatrace.reportCrash) {
                    const crashName = errorName;
                    const reason = errorMessage;
                    Dynatrace.reportCrash(crashName, reason, stackTrace);
                }
                // Priority 3: Use reportErrorStacktrace for errors with stack traces (non-crash)
                else if (stackTrace && Dynatrace.reportErrorStacktrace) {
                    Dynatrace.reportErrorStacktrace(errorName, errorMessage, errorMessage, stackTrace);
                }
                // Priority 4: Use reportError for simple errors without stack traces
                else if (Dynatrace.reportError) {
                    // API: reportError(errorName: string, errorCode: number, platform?: Platform)
                    Dynatrace.reportError(errorName, errorCode);
                } else {
                    logger.warn('DynatraceService', 'No error reporting methods available', {
                        hasReportCrashWithException: !!Dynatrace.reportCrashWithException,
                        hasReportCrash: !!Dynatrace.reportCrash,
                        hasReportErrorStacktrace: !!Dynatrace.reportErrorStacktrace,
                        hasReportError: !!Dynatrace.reportError,
                    });
                }
            } catch (err) {
                logger.warn('DynatraceService', 'Failed to report error to Dynatrace', { err });
            }
        }
    } catch (err) {
        logger.warn('DynatraceService', 'Failed to report error', { err, message, error, context });
    }
}
